import React, { ReactNode, useState } from 'react';
import {
    HStack,
    VStack,
    Skeleton,
} from 'native-base';
import { H3, Caption } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import { faMap } from '@fortawesome/free-solid-svg-icons';
import OurPressable from '~/shared/Pressable';
import {
    GiftCard,
    GiftCardLocationResponse,
    resizedUrl,
    useAppSettings,
    useTheme,
} from '@springbig/shared';
import Image, { LogoMd } from '~/shared/Image';
import { ActionSheet, Card } from '~/shared/Theme';

type CardStyledContainerProps = {
    children: ReactNode;
};

const CardStyledContainer = ({ children }: CardStyledContainerProps) => {

    return (
        <Card secondary>
            <HStack
                style={{
                    aspectRatio: 16 / 7,
                }}
                justifyContent={'space-between'}
                alignSelf={'center'}
                width={'full'}
                height={'full'}
            >
                { children }
            </HStack>
        </Card>
    );
};

const GCCard = ({
    card,
    locations,
}: {
    card?: GiftCard,
    locations?: GiftCardLocationResponse[],
}) => {

    const [isFlipped, setIsFlipped] = useState<boolean>(false);
    const { customIcons } = useAppSettings();

    const {
        card_text,
    } = useTheme();

    return (
        <CardStyledContainer>
            <OurPressable onPress={() => setIsFlipped(!isFlipped)}>
                { !isFlipped ?
                    <VStack h={'full'} justifyContent={'space-between'}>
                        <HStack space={2}>
                            <SpIcon icon={faMap} size={24} color={card_text} />
                            <H3 text={'giftCard.locations'} />
                        </HStack>
                        <Caption
                            text={card.is_closed_loop ? 'giftCard.non_reloadable' : 'giftCard.reloadable'}
                        />
                    </VStack>
                    :
                    <ActionSheet
                        isOpen={isFlipped}
                        onClose={() => setIsFlipped(false)}
                    >
                        <VStack space={2} flex={1}>
                            <Caption text={'giftCard.used_at'} />
                            <VStack flexWrap={'wrap'} pl={4}>
                                { locations && locations.map((location) =>

                                    <Caption
                                        key={location.display_name}
                                        numberOfLines={1}
                                        textOverflow={'ellipsis'}
                                        maxWidth={'100%'}
                                    >
                                        { location.display_name }
                                    </Caption>
                                )}
                            </VStack>
                        </VStack>
                    </ActionSheet>
                }
            </OurPressable>
            <VStack
                justifyContent={'space-between'}
                alignItems={'flex-end'}
            >
                { customIcons && customIcons.GiftCardIcon ?
                    <Image uri={resizedUrl(customIcons.GiftCardIcon, 35, 35)} h={35} w={35} />
                    :
                    <LogoMd resizeMode="contain" h={35} />
                }
                <H3 fontSize={20}>
                    { card.balance }
                </H3>
            </VStack>
        </CardStyledContainer>
    );
};

export const SkeletonCard = () => {

    const { radius_sm } = useTheme();

    return (
        <>
            <CardStyledContainer>
                <VStack  w={'full'} justifyContent={'space-between'} >
                    <HStack alignItems={'center'} justifyContent={'space-between'}>
                        <HStack w={'30%'} space={1}>
                            <Skeleton h={5} w='20%' rounded={radius_sm} />
                            <Skeleton h={5} w='70%' rounded={radius_sm} />
                        </HStack>
                        <Skeleton h={10} w='15%' rounded={radius_sm} />
                    </HStack>
                    <Skeleton h={7} w={'6rem'} alignSelf={'flex-end'} rounded={radius_sm} />
                </VStack>
            </CardStyledContainer>
            <VStack alignItems={'center'}>
                <Skeleton h={4} w={'7rem'} rounded={'md'} borderRadius={5} my={2} alignSelf={'start'} />
                <Skeleton h={'2.2rem'} w={'8rem'} rounded={'lg'} mt={2} />
                <Skeleton h={5} w={'14rem'} rounded={'full'} borderRadius={5} my={4} />
                <Skeleton h={4} w={'6rem'} rounded={'full'} borderRadius={5} mb={3}/>
            </VStack>
        </>
    );
};

export default GCCard;
